// ProductCreationWizard - Complete product creation wizard container
// Integrates all step components into a cohesive multi-step product creation flow
// Based on product-merchant-crud.html wizard design patterns

import React, { useCallback, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactRouterPrompt from "react-router-prompt";
import WizardProgressStepper from "../WizardProgressStepper/WizardProgressStepper";
import WizardHeader from "src/shared/forms/components/WizardHeader/WizardHeader";
import WizardNavigation from "src/shared/forms/components/WizardNavigation/WizardNavigation";
import { UnsavedChangesModal, ConfirmationModal } from "src/shared/components";
import {
  BasicInfoStep,
  ClassificationStep,
  SalesInfoStep,
  ShippingStep,
  ReviewStep,
  type BasicInfoData,
  type ClassificationData,
  type SalesInfoData,
  type ShippingData,
  type ReviewData,
} from "../WizardSteps";

// Import utilities
import { WIZARD_STEPS, TOTAL_STEPS, EMPTY_FORM_DATA, createDefaultFormData } from "../../utils/wizardConstants";
import { validateStep, validateAllSteps } from "../../utils/wizardValidation";
import { clearPersistedData } from "../../utils/wizardPersistence";
import {
  useWizardState,
  useWizardNavigation,
  useWizardUnsavedChanges,
  useWizardPersistence,
  useStepDataHandlers
} from "../../utils/wizardHooks";

// Complete product data interface combining all steps
export interface ProductData {
  basicInfo: BasicInfoData;
  classification: ClassificationData;
  salesInfo: SalesInfoData;
  shipping: ShippingData;
  review: ReviewData;
}

export interface ProductCreationWizardProps {
  /** Initial product data (for editing existing products) */
  initialData?: Partial<ProductData>;
  /** Called when wizard is submitted successfully */
  onSubmit: (data: ProductData) => void | Promise<void>;
  /** Called when wizard is cancelled */
  onCancel?: () => void;
  /** Whether the wizard is in loading state */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Component ID */
  id?: string;
  /** Test ID */
  "data-testid"?: string;
  /** Whether to persist data in localStorage */
  persistData?: boolean;
  /** Custom persistence key */
  persistKey?: string;
  /** Mode: create or edit */
  mode?: "create" | "edit";
}

export interface ProductCreationWizardRef {
  openConfirmationModal: () => void;
}

const ProductCreationWizard = forwardRef<ProductCreationWizardRef, ProductCreationWizardProps>(({
  initialData = {},
  onSubmit,
  onCancel,
  loading = false,
  className = "",
  id,
  "data-testid": dataTestId,
  persistData = false,
  persistKey = "productWizard",
  mode = "create",
}, ref) => {
  // React Router hook for navigation
  const navigate = useNavigate();

  // Use custom hooks for state management
  const { formData, setFormData, resetKey, resetForm, updateFormData } = useWizardState(initialData, mode);
  
  const {
    currentStep,
    setCurrentStep,
    stepErrors,
    setStepErrors,
    handleStepClick: handleStepClickBase,
    handlePrevious,
    handleNext: handleNextBase,
    clearStepErrors,
    resetNavigation
  } = useWizardNavigation(TOTAL_STEPS);

  const {
    localHasUnsavedChanges,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    pendingLocation,
    setPendingLocation,
    updateUnsavedChangesState,
    checkForUnsavedChanges,
    unregisterComponent,
    isInitialLoadRef,
    setInitialLoadComplete,
    resetInitialLoad
  } = useWizardUnsavedChanges("ProductCreationWizard");

  const {
    loadInitialData,
    persistFormData,
    handleBeforeUnload,
    clearPersistedData: clearPersisted
  } = useWizardPersistence(persistData, persistKey, mode);

  // Get step data handlers
  const {
    handleBasicInfoChange,
    handleClassificationChange,
    handleSalesInfoChange,
    handleShippingChange,
    handleReviewChange
  } = useStepDataHandlers(
    formData,
    updateFormData,
    checkForUnsavedChanges,
    clearStepErrors,
    isInitialLoadRef
  );

  // Confirmation modal for product creation
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Track submission state to prevent unsaved changes modal during form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Wrapper for handleStepClick to pass formData
  const handleStepClick = useCallback((stepNumber: number) => {
    handleStepClickBase(stepNumber, formData);
  }, [handleStepClickBase, formData]);

  // Wrapper for handleNext to pass formData
  const handleNext = useCallback(() => {
    handleNextBase(formData);
  }, [handleNextBase, formData]);

  // Load initial persisted data
  useEffect(() => {
    if (persistData && isInitialLoadRef.current) {
      const restoredData = loadInitialData(initialData);
      if (restoredData) {
        setFormData(restoredData);
        updateUnsavedChangesState(true); // Mark as having unsaved changes since this is draft data
      }
    }
    setInitialLoadComplete();
  }, [persistData, loadInitialData, initialData, setFormData, updateUnsavedChangesState, setInitialLoadComplete]);

  // Handle initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0 && mode === 'edit') {
      console.log('🔄 Initializing wizard with edit data:', initialData);
      
      const editFormData = createDefaultFormData(initialData, mode);
      setFormData(editFormData);
      
      // In edit mode, don't mark as having unsaved changes initially
      updateUnsavedChangesState(false);
      
      console.log('✅ Edit form initialized with data');
    }
  }, [initialData, mode, setFormData, updateUnsavedChangesState]);

  // Persist form data whenever it changes
  useEffect(() => {
    if (persistData && formData) {
      persistFormData(formData);
      // Also check for unsaved changes whenever form data updates
      if (!isInitialLoadRef.current) {
        checkForUnsavedChanges(formData);
      }
    }
  }, [persistData, formData, persistFormData, checkForUnsavedChanges, isInitialLoadRef]);

  // Browser refresh/reload detection
  useEffect(() => {
    const handleBeforeUnloadEvent = (event: BeforeUnloadEvent) => {
      return handleBeforeUnload(event, localHasUnsavedChanges);
    };

    window.addEventListener("beforeunload", handleBeforeUnloadEvent);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnloadEvent);
    };
  }, [localHasUnsavedChanges, handleBeforeUnload]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      unregisterComponent();
    };
  }, [unregisterComponent]);


  // Handle modal actions
  const handleDiscardChanges = useCallback(() => {
    // Clear localStorage immediately
    if (persistData) {
      clearPersisted();
    }

    // Reset form to completely empty state
    setFormData(EMPTY_FORM_DATA);
    updateUnsavedChangesState(false);
    setShowUnsavedChangesModal(false);
    resetNavigation();
    resetForm();
    resetInitialLoad();

    // Navigate to pending location if there is one
    if (pendingLocation) {
      const targetPath = pendingLocation.pathname;
      setPendingLocation(null);
      navigate(targetPath);
    }
  }, [
    pendingLocation,
    navigate,
    persistData,
    clearPersisted,
    updateUnsavedChangesState,
    setFormData,
    resetNavigation,
    resetForm,
    resetInitialLoad
  ]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setPendingLocation(null);
  }, [setShowUnsavedChangesModal, setPendingLocation]);




  const handleSubmit = async () => {
    // Validate all steps with strict validation for submission
    const allErrors = validateAllSteps(formData);

    if (Object.keys(allErrors).length > 0) {
      setStepErrors(allErrors);
      // Go to first step with errors
      const firstErrorStep = Math.min(...Object.keys(allErrors).map(Number));
      setCurrentStep(firstErrorStep);
      return;
    }

    // Show confirmation modal instead of directly submitting
    setShowConfirmationModal(true);
  };

  useImperativeHandle(ref, () => ({
    openConfirmationModal: () => {
      handleSubmit();
    }
  }));

  const handleConfirmSubmit = async () => {
    // Close confirmation modal
    setShowConfirmationModal(false);

    // Set submission state to prevent unsaved changes modal during navigation
    setIsSubmitting(true);
    updateUnsavedChangesState(false);

    // Submit the data
    try {
      // Clear persisted data before submission to prevent conflicts
      if (persistData) {
        clearPersisted();
      }

      // Submit data - parent component will handle navigation after success
      await onSubmit(formData);
      // If we reach here, submission was successful and parent will navigate
      // No need to reset isSubmitting as component will unmount during navigation
    } catch (error) {
      console.error("Failed to submit:", error);
      // Reset submission state and re-enable unsaved changes tracking if submission failed
      setIsSubmitting(false);
      checkForUnsavedChanges(formData);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmationModal(false);
  };



  const renderStep = () => {
    const commonProps = {
      errors: stepErrors[currentStep] || [],
      isActive: true,
      onValidate: () => validateStep(currentStep, formData),
    };

    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            key={`basic-info-${resetKey}`}
            {...commonProps}
            data={formData.basicInfo}
            onChange={handleBasicInfoChange}
          />
        );
      case 2:
        return (
          <ClassificationStep
            key={`classification-${resetKey}`}
            {...commonProps}
            data={formData.classification}
            onChange={handleClassificationChange}
            onDataChange={handleClassificationChange}
            allData={formData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            isFirst={false}
            isLast={false}
            stepIndex={currentStep - 1}
            totalSteps={TOTAL_STEPS}
          />
        );
      case 3:
        return (
          <SalesInfoStep
            key={`sales-info-${resetKey}`}
            {...commonProps}
            data={formData.salesInfo}
            onChange={handleSalesInfoChange}
          />
        );
      case 4:
        return (
          <ShippingStep
            key={`shipping-${resetKey}`}
            {...commonProps}
            data={formData.shipping}
            onDataChange={handleShippingChange}
            allData={formData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            isFirst={false}
            isLast={false}
            stepIndex={currentStep - 1}
            totalSteps={TOTAL_STEPS}
          />
        );
      case 5:
        return (
          <ReviewStep
            key={`review-${resetKey}`}
            {...commonProps}
            data={formData.review}
            onDataChange={handleReviewChange}
            allData={formData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            isFirst={false}
            isLast={true}
            stepIndex={currentStep - 1}
            totalSteps={TOTAL_STEPS}
            onEditStep={handleStepClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`tw-flex tw-flex-1 tw-flex-col ${className}`}
      id={id}
      data-testid={dataTestId}
    >
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onCancel={handleCancelNavigation}
        onDiscard={handleDiscardChanges}
        title="Unsaved Changes"
        message="You have unsaved changes in your product form. Are you sure you want to leave? All unsaved data will be lost."
      />

      {/* Confirmation Modal for Product Creation/Update */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={handleCancelSubmit}
        onConfirm={handleConfirmSubmit}
        title={mode === 'edit' ? 'Update Product' : 'Create Product'}
        message={
          mode === 'edit' 
            ? 'Are you sure you want to update this product? Please review all details before confirming. Once updated, you will be redirected to the merchant product page.'
            : 'Are you sure you want to create this product? Please review all details before confirming. Once created, you will be redirected to the merchant product page.'
        }
        cancelText="Cancel"
        confirmText={mode === 'edit' ? 'Update Product' : 'Create Product'}
        confirmVariant="primary"
      />

      {/* Navigation blocking with ReactRouterPrompt for all navigation types */}
      <ReactRouterPrompt when={localHasUnsavedChanges && !isSubmitting}>
        {({ isActive, onCancel, onConfirm }) => (
          <UnsavedChangesModal
            isOpen={isActive}
            onCancel={() => onCancel(false)}
            onDiscard={() => {
              handleDiscardChanges(); // Use the proper reset function
              onConfirm(true);
            }}
            title="Unsaved Changes"
            message="You have unsaved changes in your product form. Are you sure you want to leave? All unsaved data will be lost."
          />
        )}
      </ReactRouterPrompt>

      {/* Wizard Header - using component matching HTML design */}
      <WizardHeader
        title={WIZARD_STEPS[currentStep - 1]?.label || ""}
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onBack={() => {
          // react-router-prompt will handle unsaved changes blocking automatically
          if (onCancel) {
            onCancel();
          }
        }}
        onClose={() => {
          // react-router-prompt will handle unsaved changes blocking automatically
          if (onCancel) {
            onCancel();
          }
        }}
        mode={mode}
      />

      {/* Progress Stepper - matching HTML design */}
      <WizardProgressStepper
        steps={WIZARD_STEPS}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      {/* Step Content - matching HTML design */}
      <main
        className="tw-flex-1 tw-overflow-y-auto"
        key={`step-content-${resetKey}`}
      >
        <div className="tw-p-3 md:tw-p-6 tw-pb-[140px] sm:tw-pb-3 md:tw-pb-6">
          <div className="tw-max-w-full sm:tw-max-w-3xl lg:tw-max-w-4xl tw-mx-auto tw-space-y-4 md:tw-space-y-6">
            {renderStep()}
          </div>
        </div>
      </main>

      {/* Navigation - matching HTML design */}
      <WizardNavigation
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onCancel={() => {
          // react-router-prompt will handle unsaved changes blocking automatically
          if (onCancel) {
            onCancel();
          }
        }}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSubmit={handleSubmit}
        loading={loading}
        mode={mode}
      />
    </div>
  );
});

export default ProductCreationWizard;
